import type { AppIcons } from '@backrs_ui/common-ui/designSystem/icons/AppIcon';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import type { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import type { College, RoiMethod } from '@backrs_ui/common-ui/src/models/college';

const BACHELORS_STRINGS = appStrings.modules.charts.returnOnInvestment;
const TEN_YEAR_STRINGS = appStrings.modules.charts.tenYearReturnOnInvestment;

export type ROIData = Pick<
  NonNullable<College['scorecardRoi']>,
  | 'roi_method'
  | 'roi_1_year'
  | 'roi_2_year'
  | 'roi_3_year'
  | 'roi_4_year'
  | 'roi_5_year'
  | 'roi_6_year'
  | 'roi_7_year'
  | 'roi_8_year'
  | 'roi_9_year'
  | 'roi_10_year'
  | 'roi_15_year'
  | 'roi_20_year'
  | 'roi_30_year'
  | 'roi_40_year'
>;

const RUNNING_ROI_YEARS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

/** Only associate and certificate rows carry the years before 10 */
export const hasRunningRoi = (roiData: ROIData) => roiData.roi_1_year != null;

type TitledParagraphs = Readonly<{
  title: string;
  paragraphs: readonly string[];
}>;

export type ROILevel = 'veryGoodROI' | 'goodROI' | 'lowROI';

export type ROILevelCard = Readonly<{
  title: string;
  chartChipLabel: string;
  chipLabelPrefix: string;
  chipLabelThreshold: string;
  chipColor: ColorToken;
  iconName: AppIcons;
  cardDescription: readonly Readonly<{ text: string; bold: boolean }>[];
}>;

export type ROIHorizonStrings = Readonly<{
  title: string;
  whatItIs: TitledParagraphs;
  whyItMatters: TitledParagraphs &
    Readonly<{ bulletPoints: readonly string[]; additionalParagraph: string }>;
  howItIsCalculated: TitledParagraphs &
    Readonly<{ bulletPoints: readonly string[]; conclusion: string; source: string }>;
}>;

export type ROIChartStrings = Readonly<{
  title: string;
  explanationTitle: string;
  whatItIs: TitledParagraphs;
  whyItMatters: TitledParagraphs & Readonly<{ bulletPoints: readonly string[] }>;
  howToInterpret: Readonly<{ title: string }>;
  modalCards: Readonly<Record<ROILevel, ROILevelCard>>;
  roiHorizonModal: ROIHorizonStrings;
  legendLabel: string;
}>;

export type ROILevelThresholds = Readonly<{
  veryGoodROI: number;
  goodROI: number;
}>;

type ROIPoint = Readonly<{
  label: string;
  value: number | null;
}>;

type ROIMethodConfig = Readonly<{
  headline: (roiData: ROIData) => number | null;
  points: (roiData: ROIData) => readonly ROIPoint[];
  /** Middle half (25th-75th percentile) of the method's schools at each point */
  shadedBounds: Readonly<{ upper: readonly number[]; lower: readonly number[] }>;
  yAxis: Readonly<{ min: number; max: number; ticks: readonly number[] }>;
  levelThresholds: ROILevelThresholds;
  strings: ROIChartStrings;
}>;

type TenYearMethod = Exclude<RoiMethod, 'bachelors'>;

const tenYearStrings = (method: TenYearMethod): ROIChartStrings => {
  const groupStrings = TEN_YEAR_STRINGS.groups[method];

  return {
    title: TEN_YEAR_STRINGS.title,
    explanationTitle: TEN_YEAR_STRINGS.explanationTitle,
    whatItIs: TEN_YEAR_STRINGS.whatItIs,
    whyItMatters: TEN_YEAR_STRINGS.whyItMatters,
    howToInterpret: TEN_YEAR_STRINGS.howToInterpret,
    modalCards: groupStrings.modalCards,
    legendLabel: groupStrings.legendLabel,
    roiHorizonModal: {
      title: TEN_YEAR_STRINGS.roiHorizonModal.title,
      whatItIs: {
        title: TEN_YEAR_STRINGS.roiHorizonModal.whatItIs.title,
        paragraphs: [
          ...TEN_YEAR_STRINGS.roiHorizonModal.whatItIs.paragraphs,
          groupStrings.shadedBandParagraph,
        ],
      },
      whyItMatters: BACHELORS_STRINGS.roiHorizonModal.whyItMatters,
      howItIsCalculated: TEN_YEAR_STRINGS.roiHorizonModal.howItIsCalculated,
    },
  };
};

const tenYearConfig = (
  method: TenYearMethod,
  config: Pick<ROIMethodConfig, 'shadedBounds' | 'yAxis' | 'levelThresholds'>,
): ROIMethodConfig => ({
  ...config,
  headline: (roiData) => (hasRunningRoi(roiData) ? (roiData.roi_10_year ?? null) : null),
  points: (roiData) =>
    RUNNING_ROI_YEARS.map((year) => ({
      label: `Year ${year}`,
      value: roiData[`roi_${year}_year`] ?? null,
    })),
  strings: tenYearStrings(method),
});

const BACHELORS_HORIZONS = [10, 15, 20, 30, 40] as const;

const ROI_METHOD_CONFIG: Readonly<Record<RoiMethod, ROIMethodConfig>> = {
  bachelors: {
    headline: (roiData) => roiData.roi_15_year ?? null,
    points: (roiData) =>
      BACHELORS_HORIZONS.map((horizon) => ({
        label: `${horizon} Years`,
        value: roiData[`roi_${horizon}_year`] ?? null,
      })),
    shadedBounds: {
      upper: [28000, 371000, 717000, 1418000, 2120000],
      lower: [-45000, 224000, 489000, 1013000, 1537000],
    },
    yAxis: {
      min: -400000,
      max: 5000000,
      ticks: [-400000, 0, 500000, 1000000, 2000000, 3000000, 4000000, 5000000],
    },
    levelThresholds: { veryGoodROI: 400000, goodROI: 200000 },
    strings: BACHELORS_STRINGS,
  },
  associates: tenYearConfig('associates', {
    shadedBounds: {
      upper: [-42000, -84000, -126000, -92000, -55000, -16000, 25000, 68000, 112000, 160000],
      lower: [-48000, -97000, -145000, -109000, -72000, -36000, -2000, 36000, 73000, 113000],
    },
    yAxis: {
      min: -300000,
      max: 600000,
      ticks: [-300000, -200000, -100000, 0, 100000, 200000, 300000, 400000, 500000, 600000],
    },
    levelThresholds: { veryGoodROI: 160000, goodROI: 115000 },
  }),
  certificate: tenYearConfig('certificate', {
    shadedBounds: {
      upper: [-41000, -9000, 24000, 59000, 95000, 132000, 170000, 211000, 252000, 295000],
      lower: [-47000, -16000, 13000, 44000, 75000, 108000, 141000, 175000, 210000, 247000],
    },
    yAxis: {
      min: -200000,
      max: 600000,
      ticks: [-200000, -100000, 0, 100000, 200000, 300000, 400000, 500000, 600000],
    },
    levelThresholds: { veryGoodROI: 295000, goodROI: 245000 },
  }),
};

// A row without roi_method is bachelor's ROI; a method this build doesn't know has no config
export const roiMethodConfigFor = (roiData: ROIData): ROIMethodConfig | null => {
  const method = roiData.roi_method ?? 'bachelors';

  return Object.prototype.hasOwnProperty.call(ROI_METHOD_CONFIG, method)
    ? ROI_METHOD_CONFIG[method]
    : null;
};

export const roiHeadline = (roiData: ROIData) =>
  roiMethodConfigFor(roiData)?.headline(roiData) ?? null;
